import axios from 'axios';
import * as Sentry from '@sentry/react';

// In local dev, auto-derive API base from current hostname so both
// localhost and LAN IP (phone testing) work without changing .env.
// Production always uses the explicit REACT_APP_API_BASE env var.
function getApiBase() {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  return window.location.origin.replace(':3000', ':8080');
}

export const API_BASE = getApiBase();

// withCredentials lets the browser send the HttpOnly hr_session cookie the
// server sets at login. Production is same-origin so this is a no-op there,
// but local dev talks from :3000 to :8080 and would otherwise drop the cookie.
// That cookie is what keeps people signed in on iOS: WebKit wipes localStorage
// after 7 days without a visit, and every iOS browser is WebKit.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

let authToken = null;
try {
  var stored = localStorage.getItem('authData');
  if (stored) {
    authToken = JSON.parse(stored).token || null;
  }
} catch (e) {}

/// Stand-in stored in place of a real token when the session was restored from
/// the HttpOnly hr_session cookie. The cookie cannot be read by JavaScript, by
/// design, but ~8 components decide `isLoggedIn` from the truthiness of
/// authTokens.token, so a placeholder keeps all of them working without
/// echoing the real credential back into localStorage where XSS could read it.
/// The interceptor below makes sure it is never actually sent as a bearer.
export const COOKIE_SESSION = '__hr_cookie_session__';

export function setApiAuthToken(token) {
  authToken = token;
}

api.interceptors.request.use((config) => {
  // A real token goes in the header. For a cookie-restored session we send no
  // header at all and let the browser attach hr_session, which the server
  // falls back to when Authorization is absent.
  if (authToken && authToken !== COOKIE_SESSION) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

// Track consecutive network failures -- only show connection banner after
// multiple failures to avoid false alarms on transient errors (e.g. phone
// waking from sleep). Also starts a fallback timer on first failure so the
// banner still shows even if few requests are in flight.
var networkFailCount = 0;
var NETWORK_FAIL_THRESHOLD = 3;
var networkFailTimer = null;

// Exported for tests only
export function _resetNetworkFailCount() {
  networkFailCount = 0;
  if (networkFailTimer) {
    clearTimeout(networkFailTimer);
    networkFailTimer = null;
  }
}

api.interceptors.response.use(
  (response) => {
    if (networkFailCount > 0) {
      networkFailCount = 0;
      if (networkFailTimer) {
        clearTimeout(networkFailTimer);
        networkFailTimer = null;
      }
      window.dispatchEvent(new Event('network-recovered'));
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Don't log out for music API errors — those are provider token issues, not app auth issues
      var url = (error.config && error.config.url) || '';
      var isMusicEndpoint = url.indexOf('/api/music/') !== -1;
      if (!isMusicEndpoint) {
        // Dispatch event so React auth context handles logout gracefully
        // instead of hard-wiping localStorage and redirecting (which caused
        // logout-on-refresh due to race conditions with token initialization)
        window.dispatchEvent(new Event('auth-expired'));
      }
    }
    if (!error.response && error.message === 'Network Error') {
      networkFailCount++;
      if (networkFailCount >= NETWORK_FAIL_THRESHOLD) {
        window.dispatchEvent(new Event('network-error'));
      } else if (networkFailCount === 1 && !networkFailTimer) {
        // Fallback: if no successful request clears the count within 5s,
        // show the banner even if threshold wasn't reached (few requests in flight)
        networkFailTimer = setTimeout(function () {
          networkFailTimer = null;
          if (networkFailCount > 0) {
            window.dispatchEvent(new Event('network-error'));
          }
        }, 5000);
      }
    }
    if (error.response && error.response.status >= 500) {
      Sentry.captureException(error);
    }
    return Promise.reject(error);
  }
);

export default api;
